import { resolveImagePreloadLinks } from 'src/shared/ds';
import type { PrefetchedDataProps, PrefetchedUserDataProps } from 'src/shared/prefetch';
import { withPrefetchedData } from 'src/shared/prefetch';

import type {
	PetitionDetailsPagePrefetchedContext,
	PetitionDetailsPagePrefetchedUserContext,
} from 'src/app/pages/petitionDetails/pageContext';
import { prefetchPageContext, prefetchPageUserContext } from 'src/app/pages/petitionDetails/pageContext';
import { isLoaded } from 'src/app/shared/utils/async';

import { CommonPetitionDetails } from './CommonPetitionDetails';
import {
	buildMetaContext,
	petitionDetailsPageAlternateUrls,
	petitionDetailsPageCanonical,
	petitionDetailsPageMetadata,
	petitionDetailsPageSchemaOrg,
	petitionDetailsPageTitle,
} from './shared';
import type { PetitionDetailsMetaContext } from './shared';

type PrefetchedData = PetitionDetailsPagePrefetchedContext & {
	perimeterx?: { petitionId: string };
	metaContext: PetitionDetailsMetaContext;
	bestShareVisual: boolean;
};

/**
 * Main entry point for human users on the `/p/:slug` route.
 */
export const PetitionDetails = withPrefetchedData(
	function PetitionDetails({
		prefetchedData,
		prefetchedUserData,
	}: PrefetchedDataProps<PrefetchedData> & PrefetchedUserDataProps<PetitionDetailsPagePrefetchedUserContext>) {
		return <CommonPetitionDetails prefetchedData={prefetchedData} prefetchedUserData={prefetchedUserData} />;
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchData: async (context) => {
			// oxlint-disable-next-line typescript/no-non-null-assertion
			const data = await prefetchPageContext(context.params.slug!, context);
			return {
				...data,
				perimeterx: isLoaded(data.pageData) ? { petitionId: data.pageData.pageData.petition.id } : undefined,
				metaContext: buildMetaContext(context),
				bestShareVisual: context.path.endsWith('/bsv'),
			};
		},
		// oxlint-disable-next-line typescript/no-non-null-assertion
		prefetchUserData: async (context) => prefetchPageUserContext(context.params.slug!, context),
		pageTitle: (ctx) => petitionDetailsPageTitle(ctx),
		pageCanonical: (ctx) => petitionDetailsPageCanonical(ctx),
		pageMetadata: ({ prefetchedData, i18n, getStaticAssetUrl }) =>
			petitionDetailsPageMetadata({
				prefetchedData,
				i18n,
				getStaticAssetUrl,
				metaContext: prefetchedData.metaContext,
				bestShareVisual: prefetchedData.bestShareVisual,
			}),
		pageSchemaOrg: (ctx) =>
			petitionDetailsPageSchemaOrg({
				prefetchedData: ctx.prefetchedData,
				resolveUrl: ctx.resolveUrl,
				defineArticle: ctx.defineArticle,
				defineBreadcrumb: ctx.defineBreadcrumb,
				definePerson: ctx.definePerson,
			}),
		pageAlternateUrls: (ctx) => petitionDetailsPageAlternateUrls(ctx),
		pageImagePreloadLinks: ({ prefetchedData }) => {
			if (prefetchedData.bestShareVisual) return undefined;
			if (!isLoaded(prefetchedData.pageData)) return undefined;
			const { petition } = prefetchedData.pageData.pageData;
			const { photo } = petition;
			if (!photo || photo.petitionSmall.processing || photo.petitionMedium.processing) return undefined;
			return resolveImagePreloadLinks([
				{ href: { default: photo.petitionSmall.url, md: photo.petitionMedium.url }, fetchPriority: 'high' },
			]);
		},
	},
);
